import { createContext, useContext } from 'react';

/** What a step's own control asks of the Ask Sigora drawer. */
export interface OpenAskSigoraRequest {
  /** Words for Sigora. Absent or blank: open the drawer and focus the composer, nothing else. */
  message?: string;
  /** Send `message` on the thread on screen now, as if the researcher had typed it and pressed Send. */
  autoSend?: boolean;
}

/**
 * Opens the Ask Sigora drawer on the thread on screen, with words for Sigora when given.
 *
 * Without `autoSend` the words go into the composer if it is empty, and are declined if not: what
 * the researcher typed there is newer than anything offered. With it they are sent once, as the
 * researcher's own message, when the thread is idle and nothing runs on any other step, and the
 * conversation scrolls to them as it does after Send. When the thread's first transcript read is
 * still out, that is decided when it lands. Whenever the words cannot be sent then — a turn running
 * here or on another step, or the Brief, whose questions are never sent for the researcher — or the
 * server refuses them, they go into the composer instead, after a blank line when it already holds
 * words, so nothing is lost and nothing is queued.
 *
 * A request still undecided when the researcher leaves the step is dropped, and a second request
 * replaces the first.
 */
export type OpenAskSigora = (request?: OpenAskSigoraRequest) => void;

/**
 * The shell's open-and-send, for any component under the step panel. The default does nothing, so
 * a step drawn outside the shell — in its own unit test — needs no provider.
 */
export const AskSigoraContext = createContext<OpenAskSigora>(() => undefined);

/** The shell's open-and-send. Its identity is stable across renders. */
export const useOpenAskSigora = (): OpenAskSigora => useContext(AskSigoraContext);
